import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LineChart } from '@mui/x-charts/LineChart';

import { netCashFlowReportService } from '../../services/netCashFlowReportService';
import type { NetCashFlowBucket, NetCashFlowTotals, ReportGroupBy } from '../../types/report';
import { createEmptyFilters } from '../activity/types';
import { ReportDateRangeControls } from './ReportDateRangeControls';
import { useReportDateRange, toIsoDate } from './useReportDateRange';

/**
 * Рахує кінець дати bucket-а за його початком і groupBy — потрібно для клікабельного
 * переходу до журналу активностей з фільтром по точному періоду.
 */
function getBucketDateRange(periodStart: string, groupBy: ReportGroupBy): { from: string; to: string } {
  const start = new Date(`${periodStart}T00:00:00`);
  const end = new Date(start);

  if (groupBy === 'week') {
    end.setDate(end.getDate() + 6);
  } else if (groupBy === 'month') {
    // date_trunc('month', ...) дає перше число місяця — беремо останній день того ж місяця
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
  }
  // day: toDate === fromDate, end лишається без змін

  return { from: toIsoDate(start), to: toIsoDate(end) };
}

export default function NetCashFlowReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const companyId = Number(id);

  const {
    data: { preset, from, to },
    actions: { setFrom, setTo, handlePresetChange },
  } = useReportDateRange();
  const [groupBy, setGroupBy] = useState<ReportGroupBy>('day');

  const [buckets, setBuckets] = useState<NetCashFlowBucket[]>([]);
  const [totals, setTotals] = useState<NetCashFlowTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleBucketClick = (bucket: NetCashFlowBucket) => {
    const { from: bucketFrom, to: bucketTo } = getBucketDateRange(bucket.period, groupBy);
    navigate(`/companies/${companyId}/activities`, {
      state: {
        activityFilters: {
          ...createEmptyFilters(),
          fromDate: bucketFrom,
          toDate: bucketTo,
        },
      },
    });
  };

  useEffect(() => {
    if (!companyId) return;
    let isCancelled = false;

    setLoading(true);
    setError(null);

    netCashFlowReportService.getReport(companyId, from, to, groupBy)
      .then(response => {
        if (!isCancelled) {
          setBuckets(response.buckets);
          setTotals(response.totals);
        }
      })
      .catch(err => {
        console.error('Failed to load net cash-flow report:', err);
        if (!isCancelled) setError('Не вдалося завантажити звіт');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => { isCancelled = true; };
  }, [companyId, from, to, groupBy]);

  const xLabels = useMemo(() => buckets.map(b => b.period), [buckets]);
  const netSeries = useMemo(() => buckets.map(b => b.totalNet), [buckets]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/reports`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Рух коштів (Net)
        </Typography>
      </Box>

      <ReportDateRangeControls
        preset={preset}
        onPresetChange={handlePresetChange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totals && !loading && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Купівлі за період</Typography>
            <Typography variant="h5">${totals.totalBuy.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Продажі за період</Typography>
            <Typography variant="h5">${totals.totalSell.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Чистий рух за період</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: totals.totalNet >= 0 ? 'success.main' : 'error.main' }}>
              ${totals.totalNet.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : buckets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Немає даних за обраний період</Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <LineChart
              height={300}
              xAxis={[{ data: xLabels, scaleType: 'band' }]}
              series={[{ data: netSeries, label: 'Чистий рух', color: theme.palette.primary.main }]}
            />
          </Paper>

          <Paper sx={{ overflow: 'auto', maxHeight: 400 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead" sx={{ position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                <Box component="tr">
                  {['Період', 'Купівлі', 'Продажі', 'Чистий рух'].map(header => (
                    <Box
                      component="th"
                      key={header}
                      sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider', '&:first-of-type': { textAlign: 'left' } }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {buckets.map(bucket => (
                  <Tooltip
                    key={bucket.period}
                    title="Переглянути активності за цей період"
                    arrow
                    placement="top"
                  >
                    <Box
                      component="tr"
                      onClick={() => handleBucketClick(bucket)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{bucket.period}</Box>
                      <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${bucket.totalBuy.toFixed(2)}</Box>
                      <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${bucket.totalSell.toFixed(2)}</Box>
                      <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600, color: bucket.totalNet >= 0 ? 'success.main' : 'error.main' }}>
                        ${bucket.totalNet.toFixed(2)}
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
